import type { ReactNode } from 'react'
import { CropPlaceholder, LOW_CONFIDENCE } from '../Page'

/**
 * Board 06. Every state on it is a designed part of the product rather than a
 * fallback, and they all obey one rule: say what happened, say what it means
 * for the work in hand, and give the one action that moves it forward.
 *
 * No spinner without a count. No denial without a reason. No empty without a
 * first step. Every figure printed here is passed in by the caller from the
 * API; nothing in this file invents one.
 */

/** The panel every state on 6.7 is drawn in: kind, title, body, mono footer. */
export function StatePanel({
  kind,
  title,
  children,
  foot,
  tone = 'neutral',
  className = '',
}: {
  kind: string
  title: string
  children: ReactNode
  foot?: ReactNode
  tone?: 'neutral' | 'error'
  className?: string
}) {
  return (
    <section
      className={`flex flex-col rounded-[--radius-md] border border-rule2 bg-paper px-[19px] pb-[19px] pt-[17px] ${className}`}
    >
      <div
        className={`mb-[11px] font-mono text-[9.5px] uppercase tracking-[0.12em] ${
          tone === 'error' ? 'text-mark' : 'text-ink2'
        }`}
      >
        {kind}
      </div>
      <h2 className="mb-2 text-[19px] font-semibold leading-[1.2]">{title}</h2>
      {children}
      {foot && (
        <div className="mt-auto pt-[13px] font-mono text-[10px] leading-[1.65] text-ink2">
          {foot}
        </div>
      )}
    </section>
  )
}

/** The paragraph the boards set inside a state panel. */
export function StateText({ children }: { children: ReactNode }) {
  return <p className="mb-[13px] text-[15px] leading-[1.5] text-ink2">{children}</p>
}

export interface StateAction {
  label: string
  onClick?: () => void
  href?: string
  title?: string
}

/**
 * The action row. The first is the primary and there is only ever one, which
 * is what makes "the one action that moves it forward" legible.
 */
export function StateActions({ actions }: { actions: StateAction[] }) {
  return (
    <div className="flex flex-wrap gap-[7px]">
      {actions.map((action, index) => {
        const className =
          index === 0
            ? 'btn-primary px-3.5 py-2 text-[14px] no-underline'
            : 'btn-secondary px-[13px] py-2 text-[14px] font-normal no-underline'
        if (action.href) {
          return (
            <a key={action.label} href={action.href} className={className} title={action.title}>
              {action.label}
            </a>
          )
        }
        return (
          <button
            key={action.label}
            type="button"
            className={className}
            title={action.title}
            onClick={action.onClick}
          >
            {action.label}
          </button>
        )
      })}
    </div>
  )
}

/**
 * A counted progress bar. The count is the point: a bar without a figure
 * beside it is a spinner with extra steps.
 */
export function CountedProgress({
  percent,
  label,
  right,
  tone = 'accent',
  className = '',
}: {
  percent: number
  label: ReactNode
  right?: ReactNode
  tone?: 'accent' | 'mark'
  className?: string
}) {
  const clamped = Math.max(0, Math.min(100, percent))
  return (
    <div className={`mb-[13px] ${className}`}>
      <div className="mb-1.5 flex justify-between font-mono text-[10.5px] text-ink2">
        <div className="text-body">{label}</div>
        {right !== undefined && <div>{right}</div>}
      </div>
      <div
        className="h-1.5 overflow-hidden rounded-[1px] bg-n300"
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="h-full"
          style={{
            width: `${clamped}%`,
            background: tone === 'mark' ? 'var(--color-accent-2-700)' : 'var(--jl-accent)',
          }}
        />
      </div>
    </div>
  )
}

/** A dense mono key/value grid at the boards' 11.5px, used inside state panels. */
export function MonoRecord({
  rows,
  className = '',
}: {
  rows: [string, ReactNode][]
  className?: string
}) {
  return (
    <div
      className={`grid grid-cols-[auto_1fr] gap-x-3.5 gap-y-[5px] font-mono text-[11.5px] leading-[1.5] ${className}`}
    >
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <div className="text-ink2">{label}</div>
          <div>{value}</div>
        </div>
      ))}
    </div>
  )
}

// --- 6.7 empty -------------------------------------------------------------

/**
 * Nothing clipped yet. A project can exist with nothing in it, because a
 * project is a plan rather than a result, so this is not an error.
 */
export function EmptyClippings({
  onOpenLatest,
  latestLabel,
}: {
  onOpenLatest?: () => void
  latestLabel?: string | null
}) {
  return (
    <StatePanel
      kind="empty · no clippings yet"
      title="Nothing clipped yet."
      foot="PROJECTS CAN BE CREATED EMPTY · A PROJECT IS A PLAN, NOT A RESULT."
    >
      <StateText>
        A clipping is a region of a page with its citation already attached. Select a passage in
        the viewer, or clip straight from a search result.
      </StateText>
      <div
        className="mb-[13px] rounded-[--radius-md] border border-dashed border-rule2 px-[15px] py-3.5"
        style={{
          background:
            'repeating-linear-gradient(135deg,var(--color-surface) 0 6px,var(--color-bg) 6px 12px)',
        }}
      >
        <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.1em] text-ink2">
          How a clipping is made
        </div>
        <ol className="font-mono text-[11.5px] leading-[1.7] text-body">
          <li>1&nbsp;&nbsp;open a page</li>
          <li>2&nbsp;&nbsp;press S, drag a box</li>
          <li>3&nbsp;&nbsp;it lands in a project</li>
        </ol>
      </div>
      <StateActions
        actions={[
          {
            label: latestLabel ? `Open ${latestLabel}` : 'Open the most recent edition',
            onClick: onOpenLatest,
          },
          { label: 'Search first', href: '/search' },
        ]}
      />
    </StatePanel>
  )
}

export type AlertFrequency = 'weekly' | 'monthly' | 'off'

export const ALERT_OPTIONS: { value: AlertFrequency; label: string }[] = [
  { value: 'weekly', label: 'Weekly · email when anything new matches' },
  { value: 'monthly', label: 'Monthly · a digest' },
  { value: 'off', label: 'Off · re-run by hand' },
]

/** The alert radio from 6.7, used both in the empty state and on a saved row. */
export function AlertRadios({
  value,
  onChange,
  name,
  disabled = false,
  className = '',
}: {
  value: AlertFrequency
  onChange?: (next: AlertFrequency) => void
  name: string
  disabled?: boolean
  className?: string
}) {
  return (
    <div
      className={`flex flex-col gap-2 ${className}`}
      role="radiogroup"
      aria-label="Alert frequency"
    >
      {ALERT_OPTIONS.map((option) => {
        const on = option.value === value
        return (
          <label
            key={option.value}
            className={`flex items-center gap-[9px] text-[14.5px] ${
              disabled ? 'cursor-default' : 'cursor-pointer'
            }`}
          >
            <input
              type="radio"
              name={name}
              className="sr-only"
              checked={on}
              disabled={disabled}
              onChange={() => onChange?.(option.value)}
            />
            <span
              aria-hidden="true"
              className="flex h-[14px] w-[14px] flex-none items-center justify-center rounded-full border"
              style={{ borderColor: on ? 'var(--jl-accent)' : 'var(--color-neutral-400)' }}
            >
              {on && (
                <span
                  className="block h-[7px] w-[7px] rounded-full"
                  style={{ background: 'var(--jl-accent)' }}
                />
              )}
            </span>
            {option.label}
          </label>
        )
      })}
    </div>
  )
}

export function EmptySavedSearches({
  frequency,
  onFrequency,
  onSaveLast,
  canSaveLast,
}: {
  frequency: AlertFrequency
  onFrequency: (next: AlertFrequency) => void
  onSaveLast?: () => void
  canSaveLast?: boolean
}) {
  return (
    <StatePanel
      kind="empty · no saved searches"
      title="No saved searches."
      foot="ALERTS REQUIRE AN EMAIL ADDRESS ON THE ACCOUNT."
    >
      <StateText>
        Save a search and it will re-run on a schedule, telling you only what is new. Useful when
        the archive is still being ingested beneath you.
      </StateText>
      <AlertRadios
        className="mb-[13px]"
        name="empty-alert-frequency"
        value={frequency}
        onChange={onFrequency}
      />
      <StateActions
        actions={[
          canSaveLast
            ? { label: 'Save the last search', onClick: onSaveLast }
            : { label: 'Search first', href: '/search' },
        ]}
      />
    </StatePanel>
  )
}

/**
 * Nothing ingested. The publication is configured and licensed and nothing is
 * broken, which is exactly what this screen has to say rather than "0
 * results".
 */
export function EmptyEditions({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <StatePanel
      kind="empty · nothing ingested"
      title="No editions yet."
      foot={'THE READER-FACING VERSION OF THIS SCREEN SAYS “COMING SOON”, NOT “0 RESULTS”.'}
    >
      <StateText>
        This publication is configured and licensed, but no source files have been registered. An
        administrator loads the first PDFs; nothing here is broken.
      </StateText>
      <MonoRecord className="mb-[13px]" rows={rows} />
      <StateActions
        actions={[
          { label: 'Go to ingest', href: '/admin/ingest' },
          { label: 'Read the ingest guide', href: '/docs/handbook/' },
        ]}
      />
    </StatePanel>
  )
}

// --- 6.7 loading -----------------------------------------------------------

/**
 * Streaming a page. Never a full-page skeleton: the page itself is the
 * progress indicator, and the tiles at the current zoom arrive first.
 */
export function LoadingTiles({
  imageUrl,
  loaded,
  total,
  tilePx,
  ppi,
  columns = 3,
  rows = 6,
}: {
  imageUrl: string | null
  loaded: number
  total: number
  tilePx?: number | null
  ppi?: number | null
  columns?: number
  rows?: number
}) {
  const cells = columns * rows
  const filled = total > 0 ? Math.round((loaded / total) * cells) : 0
  const right = [tilePx ? `${tilePx} PX` : null, ppi ? `${ppi} PPI` : null]
    .filter(Boolean)
    .join(' · ')
  return (
    <StatePanel
      kind="loading · page tiles"
      title="Streaming the page."
      foot="NEVER A FULL-PAGE SKELETON · THE PAGE ITSELF IS THE PROGRESS INDICATOR."
    >
      <div className="mb-[13px] flex justify-center bg-well p-2">
        <div className="relative w-[132px]">
          {imageUrl ? (
            <img src={imageUrl} alt="" className="block w-full" />
          ) : (
            <div className="aspect-[400/818] w-full" style={{ background: 'var(--jl-well)' }} />
          )}
          <div
            className="absolute inset-0 grid"
            style={{
              gridTemplateColumns: `repeat(${columns},1fr)`,
              gridTemplateRows: `repeat(${rows},1fr)`,
            }}
          >
            {Array.from({ length: cells }, (_, index) => (
              <div
                key={index}
                style={{
                  background: index >= filled ? 'var(--jl-well)' : 'transparent',
                  borderRight: '1px solid rgba(224,163,176,0.26)',
                  borderBottom: '1px solid rgba(224,163,176,0.26)',
                }}
              />
            ))}
          </div>
        </div>
      </div>
      <CountedProgress
        percent={total > 0 ? (loaded / total) * 100 : 0}
        label={`${loaded} OF ${total} TILES`}
        right={right}
      />
      <div className="text-[14.5px] leading-[1.45] text-ink2">
        Tiles at the current zoom arrive first, so the region you are looking at sharpens before
        the rest of the sheet.
      </div>
    </StatePanel>
  )
}

export interface SearchPassRow {
  label: string
  state: 'done' | 'running' | 'queued'
  detail: string
}

/** A long search. Each pass adds to the list rather than replacing it. */
export function LoadingSearch({
  pagesSearched,
  pass,
  passes,
  passLabel,
  elapsedS,
  rows,
  onStop,
}: {
  pagesSearched: number
  pass: number
  passes: number
  passLabel: string
  elapsedS: number
  rows: SearchPassRow[]
  onStop?: () => void
}) {
  return (
    <StatePanel
      kind="loading · a long search"
      title={`Searching ${pagesSearched.toLocaleString()} pages.`}
      foot="THE COUNT IS ALWAYS REAL. IF WE CANNOT COUNT, WE SAY SO."
    >
      <CountedProgress
        percent={(pass / passes) * 100}
        label={`PASS ${pass} OF ${passes} · ${passLabel.toUpperCase()}`}
        right={`${elapsedS.toFixed(1)} s`}
      />
      <div className="mb-[13px] flex flex-col gap-[7px]">
        {rows.map((row) => (
          <div key={row.label} className="flex items-center gap-[9px] font-mono text-[11.5px]">
            <span
              className="h-[9px] w-[9px] flex-none rounded-full"
              style={{
                background:
                  row.state === 'done'
                    ? 'var(--jl-art)'
                    : row.state === 'running'
                      ? 'var(--jl-accent)'
                      : 'var(--color-neutral-300)',
              }}
            />
            <span className="flex-1">{row.label}</span>
            <span className="text-ink2">{row.detail}</span>
          </div>
        ))}
      </div>
      <div className="mb-[13px] text-[14.5px] leading-[1.45] text-ink2">
        Exact results are already on screen behind this. Each pass adds to the list rather than
        replacing it.
      </div>
      <StateActions actions={[{ label: 'Stop at exact matches', onClick: onStop }]} />
    </StatePanel>
  )
}

// --- 6.7 error -------------------------------------------------------------

/**
 * An error names the stage that failed, the reason and the time, and says what
 * survived. Staff see the traceback; readers see the stage.
 */
export function ErrorCard({
  title,
  rows,
  survives,
  actions,
  className = '',
}: {
  title: string
  rows: [string, string][]
  survives: ReactNode
  actions: StateAction[]
  className?: string
}) {
  const width = rows.reduce((max, row) => Math.max(max, row[0].length), 0)
  return (
    <StatePanel
      kind="error"
      tone="error"
      title={title}
      className={className}
      foot="AN ERROR NAMES THE STAGE THAT FAILED. STAFF SEE THE TRACEBACK; READERS SEE THE STAGE."
    >
      <div
        role="alert"
        className="mb-[13px] rounded-[--radius-md] px-[13px] py-[11px]"
        style={{
          border: '1px solid var(--color-accent-2-700)',
          background: 'var(--color-accent-2-100)',
        }}
      >
        <div className="whitespace-pre-wrap break-words font-mono text-[10.5px] leading-[1.7]">
          {rows.map(([label, value]) => (
            <div key={label}>{`${label.padEnd(width + 2, ' ')}${value}`}</div>
          ))}
        </div>
      </div>
      <div className="mb-[13px] text-[14.5px] leading-[1.45] text-ink2">{survives}</div>
      <StateActions actions={actions} />
    </StatePanel>
  )
}

// --- 6.4 doubt and machine text -------------------------------------------

export interface DoubtToken {
  text: string
  confidence: number
}

/**
 * A passage the recognition doubts, as 6.4 draws it. The tint marks the block
 * and the dotted rule marks the token; neither changes the letterforms, so a
 * researcher reading for a quotation sees exactly what the recognition
 * produced.
 */
export function LowConfidencePassage({
  tokens,
  confidence,
  engine,
  fontMeta,
  flagged,
  reviewState,
  onShowCorrected,
  onSuggest,
}: {
  tokens: DoubtToken[]
  confidence: number
  engine: string | null
  fontMeta: string
  flagged: string
  reviewState: string | null
  onShowCorrected?: () => void
  onSuggest?: () => void
}) {
  return (
    <section className="rounded-[--radius-md] border border-rule2 bg-paper px-[19px] pb-[19px] pt-[17px]">
      <div className="mb-3 flex items-baseline justify-between border-b border-n300 pb-[9px]">
        <h2 className="text-[14px] font-semibold">A passage the recognition doubts</h2>
        <div className="font-mono text-[11px] text-mark">{(confidence * 100).toFixed(1)}%</div>
      </div>
      <p className="doubt-block mb-3 text-[16.5px] leading-[1.62]">
        {tokens.map((token, index) => (
          <span key={`${token.text}-${index}`}>
            {index > 0 && ' '}
            <span className={token.confidence < LOW_CONFIDENCE ? 'doubt-token' : undefined}>
              {token.text}
            </span>
          </span>
        ))}
      </p>
      <MonoRecord
        className="mb-3"
        rows={[
          ['engine', `${engine ?? 'unknown'} · ${fontMeta}`],
          ['flagged', flagged],
          ['review', reviewState ? <span className="text-mark">{reviewState}</span> : 'not queued'],
        ]}
      />
      <div className="flex flex-wrap gap-[7px]">
        <button
          type="button"
          className="btn-secondary px-3 py-1.5 text-[13.5px] font-normal"
          onClick={onShowCorrected}
        >
          Show corrected
        </button>
        <button
          type="button"
          className="btn-secondary px-3 py-1.5 text-[13.5px] font-normal"
          onClick={onSuggest}
        >
          Suggest a fix
        </button>
      </div>
      <p className="mt-3 text-[14px] leading-[1.45] text-ink2">
        The tint marks the block; the dotted rule marks the token. Neither changes the letterforms,
        so a researcher reading for a quotation sees exactly what the recognition produced.
      </p>
    </section>
  )
}

/**
 * Anything a model produced. Four independent signals every time: mono type,
 * tinted ground, dashed accent rule, explicit label. Nothing a model wrote is
 * ever set in the reading serif.
 */
export function ModelTextPanel({ blocks }: { blocks: { label: string; body: ReactNode }[] }) {
  return (
    <section className="rounded-[--radius-md] border border-rule2 bg-paper px-[19px] pb-[19px] pt-[17px]">
      <div className="mb-3 flex items-baseline justify-between border-b border-n300 pb-[9px]">
        <h2 className="text-[14px] font-semibold">Text a model produced</h2>
        <div className="font-mono text-[10px] uppercase text-ink2">is_ai_feature</div>
      </div>
      {blocks.map((block) => (
        <div key={block.label} className="machine mb-[11px]">
          <div className="machine-label">{block.label}</div>
          <div>{block.body}</div>
        </div>
      ))}
      <p className="text-[14px] leading-[1.45] text-ink2">
        Four independent signals every time: mono type, tinted ground, dashed accent rule, explicit
        label. Nothing a model wrote is ever set in the reading serif. That face is reserved for
        what the paper printed.
      </p>
    </section>
  )
}

// --- 6.5 confidence, wherever a number appears -----------------------------

export function ConfidenceRows({
  rows,
  labelWidth = 178,
}: {
  rows: { label: string; percent: number }[]
  labelWidth?: number
}) {
  return (
    <div className="flex flex-col gap-[11px]">
      {rows.map((row) => {
        const low = row.percent < LOW_CONFIDENCE * 100
        return (
          <div key={row.label} className="flex items-center gap-[11px]">
            <div className="flex-none font-mono text-[11px] text-ink2" style={{ width: labelWidth }}>
              {row.label}
            </div>
            <div className="h-1.5 flex-1 overflow-hidden rounded-[1px] bg-n300" aria-hidden="true">
              <div
                className="h-full"
                style={{
                  width: `${Math.max(0, Math.min(100, row.percent))}%`,
                  background: low ? 'var(--color-accent-2-700)' : 'var(--jl-art)',
                }}
              />
            </div>
            <div
              className={`w-12 flex-none text-right font-mono text-[11.5px] ${
                low ? 'text-mark' : 'text-body'
              }`}
            >
              {row.percent.toFixed(1)}%
            </div>
          </div>
        )
      })}
    </div>
  )
}

// --- 6.6 when a crop is unavailable ---------------------------------------

/**
 * A crop that cannot be shown. The placeholder always names what belongs
 * there, page and region, and why it is absent. It never mimics a loading
 * state.
 */
export function CropUnavailable({
  lines,
  title,
  body,
}: {
  lines: string[]
  title: string
  body: ReactNode
}) {
  return (
    <div className="flex gap-3">
      <CropPlaceholder className="h-[78px] w-[104px] flex-none" lines={lines} />
      <div className="flex-1">
        <div className="mb-[5px] text-[15px] font-semibold">{title}</div>
        <div className="text-[14px] leading-[1.45] text-ink2">{body}</div>
      </div>
    </div>
  )
}
